import {useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useAppDispatch, useAppSelector} from "../../../app/hooks";
import {
    clearCompletedStream,
    selectCompletedBackgroundStreams,
    threadsSelectors,
} from "../threadsSlice";
import {llmBadJsonToGoodJson} from "../utils";
import {stripLCitations} from "../components/stream-parser/utils";
import {remark} from "remark";
import stripMarkdown from "strip-markdown";
import classes from "./BackgroundStreamNotifier.module.css";

const MAX_CHARS = 80;
const truncate = (s: string) =>
    s.length > MAX_CHARS ? `${s.slice(0, MAX_CHARS).trimEnd()}…` : s;

const toPlainText = (markdown: string): string =>
    remark()
        .use(stripMarkdown)
        .processSync(stripLCitations(markdown))
        .toString()
        .replace(/\\/g, "")
        .replace(/\s+/g, " ")
        .trim();

export function BackgroundStreamNotifier() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {t} = useTranslation();
    const completed = useAppSelector(selectCompletedBackgroundStreams);
    const entities = useAppSelector(threadsSelectors.selectEntities);

    useEffect(() => {
        if (completed.length === 0) return;
        for (const entityId of completed) {
            const entity = entities[entityId];
            const name =
                entity?.name ??
                t("common.defaultThreadTitle", {
                    defaultValue: "New Thread",
                });
            const realId = entity?.realThreadId;
            const firstAssistantContent = entity?.messages.find(
                (m) => m.role === "assistant" && m.content.length > 0
            )?.content;
            const rawBody =
                (firstAssistantContent &&
                    llmBadJsonToGoodJson(firstAssistantContent)?.content) ||
                "";
            const body = toPlainText(rawBody);

            const nId = notifications.show({
                title: truncate(name),
                message: truncate(body),
                withBorder: false,
                classNames: {
                    root: classes.root,
                    title: classes.title,
                    description: classes.description,
                },
                onClick: () => {
                    if (realId) {
                        navigate(`/chat/${realId}`);
                        notifications.hide(nId);
                    }
                },
                autoClose: 3000,
                position: "top-right",
            });

            dispatch(clearCompletedStream(entityId));
        }
    }, [completed, entities, dispatch, navigate, t]);

    return null;
}
